import * as React from "react";
import {cx}       from "emotion";

import "./admin-vouchers-route.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {DebounceCache}    from "@Common/react/debounce-cache";
import {fragments}        from "@Common/react/fragments";
import {Load2}            from "@Common/react/load2";
import {scopedState}      from "@Common/react/scoped-state";
import {scnv}             from "@Common/react/state-change-nested-value";
import {UseState}         from "@Common/react/use-state";
import {SearchInput}      from "@Common/ui-components/heavy/common/design2/search-input/search-input";
import {formatMoney1}     from "@Common/ui-components/language/format-money";
import {Select2}          from "@Common/ui-components/select-2/select-2";
import {sum, unique}      from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {indexToArr}       from "@Common/utils/index/index-to-arr";
import {values}           from "@Common/utils/objects";
import {Responsive}       from "@Common/ui-components/responsive";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";

import {faDownload}       from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}  from "@fortawesome/react-fontawesome";

import {LoadingPanel}     from "../../../../common/loading-panel/loading-panel";
import {BRANCH_LEVEL, COMPANY_LEVEL}    from "../../../../common/logic/user-level";
import {ItemGrid1}        from "../../../common/item-grid-1/item-grid-1";
import {AccountingLayout} from "../common/accounting-layout";

import {BranchSelectDropdown} from "./branch-select-dropdown/branch-select-dropdown";
import {CreateVoucherModal}   from "./create-vouchers-modal/create-voucher-modal";
import {DetailsModal}         from "./details-modal/details-modal";

export const AccountAdminVouchersRoute = () => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    ({}, next) => AccountingLayout({activeTab: "admin-vouchers", next}),
    ["filter", ({session}, next) => UseState({initValue: {
        idBranch: session?.data?.branch?.idBranch ? session.data.branch.idBranch : '',
        idRegion: session?.data?.branch?.idRegion ? session.data.branch.idRegion : '',
        search: '',
        system: '',
    }, next})],
    ["responsive", ({}, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ["vouchers", ({apis, userLevels, filter}, next) => Load2({
        keepOutdatedValue: true,
        fetch: () => {
            if(userLevels.userLevel === COMPANY_LEVEL) {
                return apis.next.vouchers.getVouchers1({})
            } 
            else {
                filter.onChange({
                    ...filter.value,
                    search: '',
                })
                return apis.next.vouchers.getVouchers1({})
            }
        },
        next,
    })],
    ["detailsModal", ({vouchers}, next) => DetailsModal({vouchers, next})],
    ["createVoucherModal", ({filter, vouchers}, next) => CreateVoucherModal({
        filter,
        onSaved: () => vouchers.reset(),
        next,
    })],
    ["voucherGroups", ({vouchers, userLevels, filter}, next) => next(vouchers.value && chain(
        values(vouchers.value),
        (_) => handleVoucherFilter({userLevels, filter, vouchers}),
        (_) => indexToArr(
            _,
            (v) => v.idAgent,
        ),
        (_) => {
            _.sort((a, b) => {
                const aLastName = a[0].agentFullName.split(" ").pop();
                const bLastName = b[0].agentFullName.split(" ").pop();
                return aLastName.localeCompare(bLastName);
            })
            _.forEach((innerArr) => innerArr.sort((a, b) => a.agentFullName.split(" ").pop().localeCompare(b.agentFullName.split(" ").pop())))
            return _;
        },
    ))],
    ["regions", ({apis, userLevels}, next) => Load2({
        fetch: async () => {
            let regions = userLevels.userLevel === COMPANY_LEVEL && await apis.next.region.getRegionList()
            regions.length > 1 && regions.unshift({name: "All regions", idRegion: "ALL"})
            return regions;
        },
        next,
    })],
    ["csvDownload", ({voucherGroups, regions}, next) => next(voucherGroups?.length > 0 && chain(
        voucherGroups,
        data => ([
            getColumns({regions}).map(col => col.label).join(","),
            ...data.map((d) => (
                d.map(p => ({
                    ...p,
                    regionName: p.regionName !== null ? p.regionName : ""
                }))
                .map(p => (
                    regions?.value?.length > 0 ? (
                        [`"${p.regionName}"`, `"${p.branchName}"`, `"${p.agentFullName}"`, `"${p.name}"`, `"${p.total}"`].join(",")
                    ) : (
                        [`"${p.branchName}"`, `"${p.agentFullName}"`, `"${p.name}"`, `"${p.total}"`].join(",")
                    )
                )).join("\n")
            )),
        ].join("\n")),
        csv => new Blob([csv], {type: 'text/csv'}),
        blob => (
            <div className="download-space">
                <a
                    className="csv-download"
                    download={getFilename()}
                    href={window.URL.createObjectURL(blob)}
                    xd-qa-type="link"
                    xd-qa-name="Voucher Report"
                >
                    <span>
                        <FontAwesomeIcon key={"download"} className="download-icon" icon={faDownload} xd-qa-type="icon" xd-qa-name="Download"/>
                    </span>
                    <span className="text" xd-qa-type="label" xd-qa-name="Voucher Report">Voucher Report</span>
                </a>
            </div>
        )
    ))],
    ({responsive, vouchers, detailsModal, filter, voucherGroups, csvDownload, regions}, next) => (
        <div className="admin-vouchers-route-6rf" onClick={() => detailsModal.closeAll()}>
            {Header({
                filter,
                systems: chain(
                    !vouchers.value?.error ? vouchers?.value?.map((v) => v.name) : null,
                    unique,
                ),
                voucherGroups, 
                csvDownload,
                regions,
                isMobile: responsive.lt("md"),
            })}
            <div className="cards" ref={detailsModal.parentAnchorRef}>
                {next()}
            </div>
        </div>
    ),
    ({vouchers}, next) => fragments(
        next(),
        vouchers?.loading && LoadingPanel({}),
    ),
    ({session, detailsModal, filter, createVoucherModal, userLevels, vouchers, voucherGroups}) => ItemGrid1({
        height: 111,
        placeholder: session?.data?.branch?.enableVouchers || userLevels.userLevel === COMPANY_LEVEL ? {
            message: filter.value.search && voucherGroups?.length === 0 && !vouchers.loading? <div>No vouchers found belonging to this user.</div> : "",
            action: {
                label: "CREATE NEW VOUCHER",
                onClick: () => {
                    scnv(filter, ["idRegion"], () => ''),
                    createVoucherModal.show()
                },
            }
        } : null,
        items: voucherGroups?.map((vouchers) => ({
            key: vouchers[0].idAgent,
            render: () => cs(
                ["detailsModal", (_, next) => detailsModal.local(vouchers[0].idAgent, next)],
                ({detailsModal}) => (
                    <div className="card-7tu" ref={detailsModal.targetRef} xd-qa-type="card" xd-qa-name={vouchers[0].idAgent}>
                        <div className="line1">
                            <div className="left">
                                <div className="name" title={vouchers[0].agentFullName ?? ""} xd-qa-type="text" xd-qa-name="Name">
                                    {vouchers[0].agentFullName ?? "--"}
                                </div>

                                {userLevels.userLevel === COMPANY_LEVEL && (
                                    <div className="branch" title={vouchers[0].branchName} xd-qa-type="text" xd-qa-name="Branch">
                                        <span onClick={() => scnv(filter, ["idBranch"], () => vouchers[0].idBranch)}>{vouchers[0].branchName}</span>
                                    </div>
                                )}
                            </div>

                            <div className="right" xd-qa-type="text" xd-qa-name="Amount">
                                {formatMoney1(sum(vouchers, (v) => +v.total))}
                                {getTotalExpiredVouchers(vouchers)}
                            </div>
                        </div>

                        <div className="action" 
                        >
                            <a {...{
                                onClick: () => {
                                    detailsModal.show(vouchers);
                                },
                            }}
                            xd-qa-type="link" xd-qa-name={userLevels.userLevel === COMPANY_LEVEL ? (
                                "View/Edit"
                            ) : (
                                session?.data?.branch?.enableVouchers 
                            ) ? (
                                "View/Edit"
                            ) : (
                                "View"
                            )}>
                                {userLevels.userLevel === COMPANY_LEVEL ? (
                                    "View/Edit"
                                ) : (
                                    session?.data?.branch?.enableVouchers 
                                ) ? (
                                    "View/Edit"
                                ) : (
                                    "View"
                                )}
                            </a>
                        </div>
                    </div>
                ),
            )
        })),
    }),
);

const Header = ({filter, systems, voucherGroups, csvDownload, regions, isMobile}) => cs(
    consumeContext("userLevels"),
    ({userLevels}) => (
        <div className={cx("header", {mobile: isMobile})}>
            {userLevels.userLevel === COMPANY_LEVEL && <div className="left">
                {regions.value?.length > 0 && SearchableSelect({
                    list: regions.value,
                    placeholder: "Please select a region",
                    getLabel: r=>r.name,
                    onSelect: async (region) => {
                        filter.onChange(!region ? {
                            ...filter.value,
                            idRegion: '',
                        } : region.idRegion === "ALL" ? {
                            ...filter.value,
                            idRegion: region.idRegion,
                        } : {
                            ...filter.value,
                            idBranch: '',
                            idRegion: region.idRegion,
                        })
                    },
                    isSelected: (r)=>filter.value?.idRegion && r.idRegion === filter.value.idRegion,
                    allowClear: true,
                    xdQaName: "Region Filter",
                })}
                {BranchSelectDropdown({filter})}
                
                {voucherGroups && !isMobile && csvDownload}
            </div>}

            <div className="right">

                <div className="control">
                    {Select2({
                        list: [
                            null,
                            ...systems??[],
                        ],
                        placeholder: "Filter by...",
                        getLabel: v=>v,
                        onSelect: async (system) => {
                            filter.onChange(!system ? {
                                ...filter.value,
                                system: '',
                            } : {
                                ...filter.value,
                                system,
                            })
                        },
                        isSelected: (v)=>filter.value.system && v === filter.value.system,
                        style: {
                            height: 26,
                        },
                        xdQaName: "Program Filter",
                    })}
                </div>

                <div className="control">
                    {cs(
                        ["debounce", ({}, next) => DebounceCache({
                            state: scopedState(filter, ["search"]),
                            delay: 100,
                            next,
                        })],
                        ({debounce}) => SearchInput({
                            state: debounce.state,
                            placeholder: "Search Vouchers by User",
                            onBlur: debounce.flush,
                            xdQaName: "Search",
                        })
                    )}
                </div>

                {userLevels.userLevel === BRANCH_LEVEL &&
                    voucherGroups && !isMobile && csvDownload}

            </div>
            {voucherGroups && isMobile && csvDownload}
        </div>
    ),
);

const handleVoucherFilter = ({userLevels, filter, vouchers}) => {
    if(filter.value.idBranch === '' && filter.value.idRegion === '' &&
        filter.value.system === '' && filter.value.search === '' && userLevels.userLevel === COMPANY_LEVEL) return [];

    let data = vouchers.value;
    if(userLevels.userLevel === COMPANY_LEVEL) {
        if(filter.value.idRegion === "ALL" || filter.value.idRegion === '') {
            if(filter.value.idBranch === "ALL" || filter.value.idBranch === '') {
                data = filter.value.search ? data.filter((s) => s.agentFullName.toLowerCase().includes(filter.value.search?.toLowerCase())) : data
                data = filter.value.system ? data.filter((v) => v.name === filter.value.system) : data
                return data;
            } 

            data = filter.value.idBranch ? data.filter((b) => b.idBranch === filter.value.idBranch) : data;
            data = filter.value.search ? data.filter((s) => s.agentFullName.toLowerCase().includes(filter.value.search?.toLowerCase())) : data
            data = filter.value.system ? data.filter((v) => v.name === filter.value.system) : data
            return data;
        }

        data = filter.value.idRegion ? data.filter((r) => r.idRegion === filter.value.idRegion) : data
        data = filter.value.idBranch ? data.filter((b) => b.idBranch === filter.value.idBranch) : data;
        data = filter.value.search ? data.filter((s) => s.agentFullName.toLowerCase().includes(filter.value.search?.toLowerCase())) : data
        data = filter.value.system ? data.filter((v) => v.name === filter.value.system) : data
        return data;
    }

    data = filter.value.search ? data.filter((s) => s.agentFullName.toLowerCase().includes(filter.value.search?.toLowerCase())) : data
    data = filter.value.system ? data.filter((v) => v.name === filter.value.system) : data
    return data;
}

const getColumns = ({regions}) => (regions.value?.length > 0) ? columns : columns.slice(1)

const columns = [
    {
        label: "REGION",
    },
    {
        label: "OFFICE NAME",
    },
    {
        label: "USER NAME",
    },
    {
        label: "PROGRAM",
    },
    {
        label: "BALANCE",
    },
];

const getFilename = () => {
    const date  = new Date;
    const year  = date.getFullYear().toString().slice(-2)
    const month = (date.getMonth() + 1) < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1
    const day   = date.getDate()
    return (
        year + '' + month + '' + day + "_voucher_xdocs_voucher_report" + ".csv"
    )
}

const getTotalExpiredVouchers = (vouchers)=> {
    const today = new Date();
    let count = 0;
    vouchers.forEach((v) => {
        const expDate = v.dateExpiration !== null ? new Date(v.dateExpiration) : new Date("Invalid Date")
        if(expDate < today && !isNaN(expDate.getTime())) {
            count++;
        }
    })
    return count > 0 ? <div className="badge" xd-qa-type="badge" xd-qa-name="Expired">{count + " Expired"}</div> : null;
}